import React, { useState, useEffect } from 'react';
import { leadGenService } from '../../services/leadGeneration';
import { crawlingService } from '../../services/crawlingService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Loading from '../common/Loading';
import GuideSystem from '../common/GuideSystem';
import FloatingGuideButton from '../common/FloatingGuideButton';
import { getGuideContent } from '../../utils/guideContent';
import { useGuide } from '../../hooks/useGuide';

const LeadsManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [socialLinks, setSocialLinks] = useState({});
  const [loadingLinks, setLoadingLinks] = useState(new Set());
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [filters, setFilters] = useState({
    contact_status: '',
    priority: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
  });

  const { isGuideVisible, hasSeenGuide, showGuide, hideGuide } = useGuide('leads'); 
  const guideData = getGuideContent('leads');

  useEffect(() => {
    loadLeads();
  }, [filters, pagination.page]);

  // Load links for all leads with place details
  useEffect(() => {
    if (leads.length > 0) {
      leads.forEach(lead => {
        const placeId = lead.place_details?.id;
        if (placeId && !socialLinks[placeId]) {
          loadSocialLinksForLead(placeId);
        }
      });
    }
  }, [leads]);

  const loadLeads = async () => {
    setLoading(true);
    const params = {
      page: pagination.page,
      page_size: pagination.page_size,
      ...filters,
    };

    Object.keys(params).forEach(key => {
      if (params[key] === '') delete params[key];
    });

    const { data, error } = await leadGenService.getLeads(params);
    
    if (data) {
      setLeads(data.leads || []);
      setPagination(prev => ({
        ...prev,
        total: data.count || 0,
      }));
    } else if (error) {
      toast.error('Failed to load leads');
      console.error('Error loading leads:', error);
    }
    
    setLoading(false);
  };

  const exportLeads = async () => {
    const { data, error } = await leadGenService.exportLeads();
    
    if (data) {
      toast.success(`Exported leads to ${data.filename}`);
    } else {
      toast.error('Failed to export leads');
    }
  };

  const loadSocialLinksForLead = async (placeId) => {
    if (loadingLinks.has(placeId) || socialLinks[placeId]) return;
    
    setLoadingLinks(prev => new Set(prev).add(placeId));
    
    try {
      const { data } = await crawlingService.getSocialMediaLinks({
        place_id: placeId,
        page_size: 50
      });
      
      if (data && data.results) {
        setSocialLinks(prev => ({
          ...prev,
          [placeId]: data.results
        }));
      }
    } catch (error) {
      console.error('Error loading social links:', error);
    } finally {
      setLoadingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(placeId);
        return newSet;
      });
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead.id));
    }
  };

  const deleteSelectedLeads = async () => {
    if (selectedLeads.length === 0) {
      toast.error('Please select leads to delete');
      return;
    }

    if (window.confirm(`Delete ${selectedLeads.length} selected lead(s)? This action cannot be undone.`)) {
      // Implement bulk delete API call here
      toast.success(`Deleted ${selectedLeads.length} leads`);
      setSelectedLeads([]);
      loadLeads();
    }
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setShowEditModal(true);
  };

  const handleUpdateLead = async (leadData) => {
    const { data, error } = await leadGenService.updateLead(selectedLead.id, leadData);
    
    if (data) {
      toast.success('Lead updated successfully');
      setShowEditModal(false);
      setSelectedLead(null);
      loadLeads();
    } else if (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleDeleteLead = (lead) => {
    setLeadToDelete(lead);
    setShowDeleteModal(true);
  };

  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;

    const { data, error } = await leadGenService.deleteLead(leadToDelete.id);
    
    if (data) {
      toast.success('Lead deleted successfully');
      loadLeads();
    } else if (error) {
      toast.error('Failed to delete lead');
    }

    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusColor = (status) => {
    const colors = {
      'not_contacted': 'bg-gray-100 text-gray-800 border-gray-200',
      'contacted': 'bg-blue-100 text-blue-800 border-blue-200',
      'responded': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'qualified': 'bg-green-100 text-green-800 border-green-200',
      'converted': 'bg-purple-100 text-purple-800 border-purple-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[status] || colors['not_contacted'];
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-gray-100 text-gray-800 border-gray-200',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'high': 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[priority] || colors['medium'];
  };

  const getSocialLinkColor = (linkType) => {
    const colors = {
      facebook: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      instagram: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
      twitter: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
      linkedin: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      youtube: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
      tiktok: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
      whatsapp: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
      email: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
      phone: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
      website: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
    };
    return colors[linkType] || 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100';
  };

  const getSocialLinkIcon = (linkType) => {
    const icons = {
      facebook: '📘',
      instagram: '📷', 
      twitter: '🐦',
      linkedin: '💼',
      youtube: '🎥',
      tiktok: '🎵',
      whatsapp: '📱',
      email: '📧',
      phone: '📞',
      website: '🌐'
    };
    return icons[linkType] || '🔗';
  };

  const extractContactContent = (url, linkType) => {
    if (linkType === 'email') {
      if (url.startsWith('mailto:')) {
        return url.replace('mailto:', '');
      }
      if (url.includes('@') && !url.includes('http')) {
        return url;
      }
    }
    
    if (linkType === 'phone') {
      if (url.startsWith('tel:')) {
        return url.replace('tel:', '');
      }
      if (/^[\+]?[0-9\(\)\-\s]+$/.test(url)) {
        return url;
      }
    }
    
    return null;
  };

  const openLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderSocialLinks = (lead) => {
    const placeId = lead.place_details?.id;
    if (!placeId) return null;

    const links = socialLinks[placeId] || [];
    const isLoadingForPlace = loadingLinks.has(placeId);

    return (
      <div className="mt-2 pt-2 border-t border-gray-100">
        {isLoadingForPlace ? (
          <div className="flex items-center py-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-xs text-gray-600">Loading...</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 select-none">
            {/* Add website first if it exists */}
            {lead.place_details?.website_uri && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openLink(lead.place_details.website_uri);
                }}
                className={`flex items-center space-x-1 px-2 py-1 rounded border text-xs hover:shadow-sm transition-all ${getSocialLinkColor('website')}`}
                title={lead.place_details.website_uri}
              >
                <span className="text-sm">{getSocialLinkIcon('website')}</span>
                <span className="font-medium capitalize">Website</span>
              </button>
            )}
            
            {/* Then show social links */}
            {links.length > 0 && links.sort((a, b) => a.link_type === 'email' ? 1 : b.link_type === 'email' ? -1 : 0).slice(0, 8).map((link) => {
              const contactContent = extractContactContent(link.url, link.link_type);
              
              return (
                <button
                  key={link.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLink(link.url);
                  }}
                  className={`flex items-center space-x-1 px-2 py-1 rounded border text-xs hover:shadow-sm transition-all ${getSocialLinkColor(link.link_type)}`}
                  title={link.url}
                >
                  <span className="text-sm">{getSocialLinkIcon(link.link_type)}</span>
                  <span className="font-medium capitalize">
                    {link.link_type === 'email' ? extractContactContent(link.url, link.link_type) || link.link_type : link.link_type}
                  </span>
                </button>
              );
            })}
            
            {links.length > 8 && (
              <span className="inline-flex items-center px-2 py-1 text-xs text-gray-500">
                +{links.length - 8} more
              </span>
            )}
            
            {!lead.place_details?.website_uri && links.length === 0 && (
              <p className="text-xs text-gray-400">No links found</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.page_size);

  if (loading && leads.length === 0) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {guideData && (
        <GuideSystem
          steps={guideData.steps}
          pageName={guideData.pageName}
          isVisible={isGuideVisible}
          onClose={hideGuide}
        />
      )}

      <FloatingGuideButton 
        onClick={showGuide}
        hasNewFeature={!hasSeenGuide}
      />

      <div className="bg-white shadow-sm border-b">
        <div className="max-w-screen mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Go to Dashboard"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </button>
            </div>
            
            <div className="flex items-center space-x-3 select-none">
              <button
                onClick={exportLeads}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Export Excel</span>
              </button>
              {selectedLeads.length > 0 && (
                <button
                  onClick={deleteSelectedLeads}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Delete ({selectedLeads.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-gray-900">{pagination.total}</div>
              <div className="text-sm text-gray-500">Total Leads</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-red-600">
                {leads.filter(l => l.contact_status === 'not_contacted').length}
              </div>
              <div className="text-sm text-gray-500">Not Contacted</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-blue-600">
                {leads.filter(l => l.contact_status === 'contacted').length}
              </div>
              <div className="text-sm text-gray-500">Contacted</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-green-600">
                {leads.filter(l => l.response_received).length}
              </div>
              <div className="text-sm text-gray-500">Responded</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-purple-600">
                {leads.filter(l => l.contact_status === 'converted').length}
              </div>
              <div className="text-sm text-gray-500">Converted</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 select-none">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Contact Status
              </label>
              <select
                value={filters.contact_status}
                onChange={(e) => setFilters(prev => ({ ...prev, contact_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Status</option>
                <option value="not_contacted">Not Contacted</option>
                <option value="contacted">Contacted</option>
                <option value="responded">Responded</option>
                <option value="qualified">Qualified</option>
                <option value="converted">Converted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({ contact_status: '', priority: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Leads List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={leads.length > 0 && selectedLeads.length === leads.length}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500 select-none">
                {selectedLeads.length > 0 ? `${selectedLeads.length} selected` : 'Select all'}
              </span>
            </div>
            <div className="text-sm text-gray-500 select-none">
              Page {pagination.page} of {totalPages}
            </div>
          </div>

          {leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <span className="text-6xl mb-4 block">👥</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
              <p className="text-gray-500">Convert some saved places to leads or adjust your filters.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {leads.map((lead) => (
                <div 
                  key={lead.id} 
                  className="p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectLead(lead.id);
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    
                    <div className="ml-3 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-base font-medium text-gray-900">
                              {lead.place_details?.display_name || 'Unknown Business'}
                            </h4>
                            
                            <div className="flex items-center space-x-1.5 select-none">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(lead.priority)}`}>
                                {lead.priority?.charAt(0).toUpperCase() + lead.priority?.slice(1)}
                              </span>
                              
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(lead.contact_status)}`}>
                                {lead.contact_status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>

                              {lead.response_received && (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border-green-200">
                                  ✅ Responded
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {lead.place_details?.formatted_address && (
                            <p className="text-sm text-gray-600 mt-0.5">
                              📍 {lead.place_details.formatted_address}
                            </p>
                          )}

                          {lead.identified_issue && (
                            <p className="text-xs text-gray-700 mt-1">
                              <span className="font-medium">Issue:</span> {lead.identified_issue}
                            </p>
                          )}

                          {(lead.contacted_via || lead.contact_date) && (
                            <p className="text-xs text-gray-600 mt-1">
                              {lead.contacted_via && (
                                <span>📞 {lead.contacted_via.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                              )}
                              {lead.contact_date && (
                                <span className="ml-3">📅 {new Date(lead.contact_date).toLocaleDateString()}</span>
                              )}
                            </p>
                          )}

                          {lead.notes && (
                            <p className="text-xs text-gray-600 mt-1 italic">
                              📝 {lead.notes.substring(0, 100)}{lead.notes.length > 100 ? '...' : ''}
                            </p>
                          )}

                          {renderSocialLinks(lead)}
                        </div>
                        
                        <div className="ml-4 flex-shrink-0 flex space-x-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleEditLead(lead)}
                            className="px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs hover:bg-blue-100"
                          >
                            Edit
                          </button>
                          
                          <button
                            onClick={() => handleDeleteLead(lead)}
                            className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded text-xs hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.page_size) + 1} to{' '}
                  {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
                  {pagination.total} leads
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    ← Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + Math.max(1, pagination.page - 2);
                    if (page > totalPages) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 border rounded text-sm ${
                          page === pagination.page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEditModal && selectedLead && (
        <EditLeadModal
          lead={selectedLead}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLead(null);
          }}
          onUpdate={handleUpdateLead}
        />
      )}

      {showDeleteModal && leadToDelete && (
        <DeleteConfirmationModal
          lead={leadToDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setLeadToDelete(null);
          }}
          onConfirm={confirmDeleteLead}
        />
      )}
    </div>
  );
};

const DeleteConfirmationModal = ({ lead, onClose, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <span className="text-2xl">🗑️</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Delete Lead</h3>
          <p className="text-sm text-gray-500 text-center mb-4">
            Are you sure you want to delete this lead? This action cannot be undone.
          </p>
          <div className="flex items-center justify-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
            >
              Delete Lead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditLeadModal = ({ lead, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    identified_issue: lead.identified_issue || '',
    priority: lead.priority || 'medium',
    contact_status: lead.contact_status || 'not_contacted',
    contacted_via: lead.contacted_via || '',
    contact_date: lead.contact_date ? lead.contact_date.split('T')[0] : '',
    response_received: lead.response_received || false,
    response_via: lead.response_via || '',
    response_date: lead.response_date ? lead.response_date.split('T')[0] : '',
    response_summary: lead.response_summary || '',
    notes: lead.notes || '',
    tags: lead.tags ? lead.tags.join(', ') : '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const updateData = {
      ...formData,
      tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
      contact_date: formData.contact_date || null,
      response_date: formData.response_date || null,
    };

    onUpdate(updateData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit Lead</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Identified Issue
              </label>
              <textarea
                value={formData.identified_issue}
                onChange={(e) => setFormData(prev => ({ ...prev, identified_issue: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                rows="3"
                placeholder="What issue or need did you identify for this business?"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Status
                </label>
                <select
                  value={formData.contact_status}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="not_contacted">Not Contacted</option>
                  <option value="contacted">Contacted</option>
                  <option value="responded">Responded</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contacted Via
                </label>
                <select
                  value={formData.contacted_via}
                  onChange={(e) => setFormData(prev => ({ ...prev, contacted_via: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select method</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="website_form">Website Form</option>
                  <option value="social_media">Social Media</option>
                  <option value="in_person">In Person</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Date
                </label>
                <input
                  type="date"
                  value={formData.contact_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="response_received"
                checked={formData.response_received}
                onChange={(e) => setFormData(prev => ({ ...prev, response_received: e.target.checked }))}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="response_received" className="ml-2 text-sm text-gray-700">
                Response received
              </label>
            </div>

            {formData.response_received && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Response Via
                    </label>
                    <select
                      value={formData.response_via}
                      onChange={(e) => setFormData(prev => ({ ...prev, response_via: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Select method</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="website_form">Website Form</option>
                      <option value="social_media">Social Media</option>
                      <option value="in_person">In Person</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Response Date
                    </label>
                    <input
                      type="date"
                      value={formData.response_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, response_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Response Summary
                  </label>
                  <textarea
                    value={formData.response_summary}
                    onChange={(e) => setFormData(prev => ({ ...prev, response_summary: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    rows="3"
                    placeholder="Summary of the response received"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                rows="3"
                placeholder="Additional notes about this lead"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Enter tags separated by commas"
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Update Lead
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeadsManagement;